import AdvancedSearchModal, {
  AdvancedSearchReturn,
  IField,
} from "../components/AdvancedSearchModal";
import * as Sentry from "@sentry/browser";
import { Table, Tag, message, Row, Col } from "antd";
import debounce from "lodash.debounce";
import moment from "moment";
import React from "react";
import { RouteComponentProps } from "react-router";
import { NavLink } from "react-router-dom";
import { AxiosResponse } from "axios";
import Card from "../components/Card";
import ExportData from "../components/ExportData";
import {
  FixedIncomePaymentInstruction,
  RowSelection,
} from "../helpers/shared-interfaces";
import {
  formatSnakeCase,
  capitalizeFirstLetter,
  generateNameFromCustomer,
  getStateColor,
  request,
  showTotalPagination,
} from "../helpers/utilities";
import {
  clearAllFilters,
  hasActiveFilters,
  parseQueryParams,
  buildQueryString,
} from "../components/TableFilters";
import { PageToolbar } from "../components/PageToolbar";

import {
  GET_FIXED_INCOME_PAYMENT_INSTRUCTION_STATE_ENUM,
  GET_FIXED_INCOME_PAYMENT_INSTRUCTION_SORT_FIELDS,
  SEARCH_FIXED_INCOME_PAYMENT_INSTRUCTIONS,
} from "../gql/fixed-income-requests";
import { ALL_USERS_QUERY } from "../gql/users";
import { DEBOUNCE_TIME } from "../helpers/constants";
import { generateDisplayErrorMessage } from "../helpers/utilities";



interface State {
  paymentInstructions: FixedIncomePaymentInstruction[];
  page: number;
  paymentInstructionRequestsCount: number;
  isLoading: boolean;
  selectedIdsToExport: React.Key[];
  selectedStates: string[];
  stateOptions: Array<{ value: string; label: string; color?: string; }>;
  selectedAssignedTo: string[];
  assignedToOptions: Array<{ value: string; label: string; }>;
  sortField: string;
  sortDirection: string;
  sortFieldOptions: Array<{ value: string; label: string; }>;
  pageSize: number;
  q: string;
  isSearchingUsers: boolean;
  showAdvancedSearchModal: boolean;
  selectedFieldsToDisplay: IField[];
  removedField?: IField;
}

const paymentInstructionColumns = [
  {
    title: "Date",
    dataIndex: "createdAt",
    key: "createdAt",
    render(createdAt: string): JSX.Element {
      return <span>{createdAt ? moment(createdAt).format("ll") : "N/A"}</span>;
    },
  },
  {
    title: "Customer",
    dataIndex: "fixedIncomeRequest",
    key: "customer",
    render(
      fixedIncomeRequest: any,
      record: FixedIncomePaymentInstruction,
    ): JSX.Element {
      return (
        <NavLink to={`/fixed-income-payment-instructions/${record.id}`}>
          {generateNameFromCustomer(fixedIncomeRequest.customer)}
        </NavLink>
      );
    },
  },
  {
    title: "Total Amount Paid",
    dataIndex: "totalAmountPaid",
    key: "totalAmountPaid",
    render(totalAmountPaid: number): JSX.Element {
      return <span>GHS {totalAmountPaid?.toLocaleString() || "0.00"}</span>;
    },
  },
  {
    title: "Status",
    dataIndex: "state",
    key: "state",
    render(state: string): JSX.Element {
      return (
        <Tag
          color={getStateColor(state)}
          style={{ textTransform: "capitalize" }}
        >
          {formatSnakeCase(capitalizeFirstLetter(state))}
        </Tag>
      );
    },
  },
  {
    title: "Product",
    dataIndex: "fixedIncomeRequest",
    key: "product",
    render(fixedIncomeRequest: any): JSX.Element {
      return <span>{fixedIncomeRequest?.product?.name || "N/A"}</span>;
    },
  },
  {
    title: "Provider",
    dataIndex: "fixedIncomeRequest",
    key: "provider",
    render(fixedIncomeRequest: any): JSX.Element {
      return fixedIncomeRequest?.provider ? (
        <NavLink to={`/providers/${fixedIncomeRequest.provider.id}`}>
          {fixedIncomeRequest.provider.name}
        </NavLink>
      ) : (
        <span>N/A</span>
      );
    },
  },
  {
    title: "Assigned To",
    dataIndex: "assignedTo",
    key: "assignedTo",
    render(assignedTo: any): JSX.Element {
      return <span>{assignedTo?.name || "Unassigned"}</span>;
    },
  },
];

export default class FixedIncomePaymentInstructions extends React.Component<
  RouteComponentProps,
  State
> {
  private _isMounted = false;

  constructor(props: RouteComponentProps) {
    super(props);
    this.state = {
      isLoading: true,
      paymentInstructions: [],
      page: 1,
      paymentInstructionRequestsCount: 0,
      selectedIdsToExport: [],
      selectedStates: [],
      stateOptions: [],
      selectedAssignedTo: [],
      assignedToOptions: [],
      sortField: "CREATED_AT",
      sortDirection: "DESC",
      sortFieldOptions: [],
      pageSize: 25,
      q: "",
      isSearchingUsers: false,
      showAdvancedSearchModal: false,
      selectedFieldsToDisplay: [],
    };
  }

  async componentDidMount(): Promise<void> {
    this._isMounted = true;

    await Promise.all([
      this.fetchStateEnums(),
      this.fetchUsers(),
      this.fetchSortFieldOptions(),
    ]);

    this.loadFromQueryParams(() => {
      this.performSearch();
    });
  }

  componentWillUnmount(): void {
    this._isMounted = false;
  }

  toggleAdvancedSearchModel = (): void => {
    this.setState((prevState) => ({
      showAdvancedSearchModal: !prevState.showAdvancedSearchModal,
    }));
  };

  handleOnAdvancedSearchError = (error: string): void => {
    message.error(error);
    this.setState({ isLoading: false });
  };

  handleOnAdvancedSearchData = (
    data: AdvancedSearchReturn,
    fields: IField[],
  ): void => {
    this.setState({
      isLoading: false,
      paymentInstructions: data.results as FixedIncomePaymentInstruction[],
      paymentInstructionRequestsCount: data.count,
      selectedFieldsToDisplay: fields,
      removedField: undefined,
    });
  };

  handleOnFieldRemove = (field: IField): void => {
    const fields = this.state.selectedFieldsToDisplay.filter(
      (f) => f.name !== field.name,
    );
    this.setState(
      { removedField: field, selectedFieldsToDisplay: fields },
      () => {
        if (fields.length === 0) this.performSearch();
      },
    );
  };

  fetchStateEnums = async (): Promise<void> => {
    try {
      const response: AxiosResponse = await request({
        query: GET_FIXED_INCOME_PAYMENT_INSTRUCTION_STATE_ENUM,
      });

      const enumValues = response.data?.data?.__type?.enumValues || [];

      const stateOptions = enumValues.map((ev: any) => ({
        value: ev.name,
        label:
          ev.description || formatSnakeCase(capitalizeFirstLetter(ev.name)),
        color: getStateColor(ev.name.toLowerCase()),
      }));

      if (this._isMounted) {
        this.setState({ stateOptions });
      }
    } catch (error) {
      Sentry.captureException(error);
      console.error("Failed to fetch state enums:", error);
    }
  };

  fetchUsers = async (q?: string): Promise<void> => {
    try {
      if (q) {
        this.setState({ isSearchingUsers: true });
      }
      const response: AxiosResponse = await request({
        query: ALL_USERS_QUERY,
        variables: { page: 1, q: q || undefined },
      });

      const results = response.data?.data?.users?.results || [];

      const assignedToOptions = results.map((user: any) => ({
        value: user.id,
        label: user.name,
      }));

      if (this._isMounted) {
        this.setState({ assignedToOptions, isSearchingUsers: false });
      }
    } catch (error) {
      Sentry.captureException(error);
      console.error("Failed to fetch users:", error);
      if (this._isMounted) {
        this.setState({ isSearchingUsers: false });
      }
    }
  };

  handleAssignedToSearch = debounce((value: string): void => {
    this.fetchUsers(value);
  }, 300);

  fetchSortFieldOptions = async (): Promise<void> => {
    try {
      const response: AxiosResponse = await request({
        query: GET_FIXED_INCOME_PAYMENT_INSTRUCTION_SORT_FIELDS,
      });

      const enumValues = response.data?.data?.__type?.enumValues || [];

      const sortFieldOptions = enumValues.map((ev: any) => ({
        value: ev.name,
        label:
          ev.description || formatSnakeCase(capitalizeFirstLetter(ev.name)),
      }));

      if (this._isMounted) {
        this.setState({ sortFieldOptions });
      }
    } catch (error) {
      Sentry.captureException(error);
      console.error("Failed to fetch sort field options:", error);
    }
  };

  performSearch = async (): Promise<void> => {
    if (!this._isMounted) return;

    const {
      q,
      page,
      pageSize,
      selectedStates,
      selectedAssignedTo,
      sortField,
      sortDirection,
    } = this.state;

    this.setState({ isLoading: true });

    try {
      const r: AxiosResponse = await request({
        query: SEARCH_FIXED_INCOME_PAYMENT_INSTRUCTIONS,
        variables: {
          q: q || null,
          page,
          numberPerPage: pageSize,
          filters: {
            states: selectedStates.length > 0 ? selectedStates : null,
            assignedToIds:
              selectedAssignedTo.length > 0 ? selectedAssignedTo : null,
          },
          orderBy: {
            field: sortField,
            direction: sortDirection,
          },
        },
      });

      const searchData = r.data?.data?.fixedIncomePaymentInstructions;

      if (!searchData) {
        if (this._isMounted) {
          message.error("Failed To Load Payment Instructions");
          this.setState({ isLoading: false });
        }
        return;
      }

      if (this._isMounted) {
        this.syncQueryStringToUrl();
        this.setState({
          isLoading: false,
          paymentInstructions: searchData.results || [],
          paymentInstructionRequestsCount: searchData.count,
        });
      }
    } catch (error) {
      if (this._isMounted) {
        this.setState({ isLoading: false });
        message.error(`Search Failed - ${generateDisplayErrorMessage(error)}`);
      }
      Sentry.captureException(error);
    }
  };

  performDelayedSearch = debounce(async (): Promise<void> => {
    return await this.performSearch();
  }, DEBOUNCE_TIME);

  loadFromQueryParams = (cb?: () => void): void => {
    const params = parseQueryParams({
      includeAssignedTo: true,
      includeSort: true,
    });

    this.setState(
      {
        q: params.q || "",
        page: params.page || 1,
        selectedStates: params.selectedStates || [],
        selectedAssignedTo: params.selectedAssignedTo || [],
        sortField: params.sortField || "CREATED_AT",
        sortDirection: params.sortDirection || "DESC",
      },
      cb,
    );
  };

  syncQueryStringToUrl = (): void => {
    const queryStringResult = buildQueryString(this.state, {
      includeAssignedTo: true,
      includeSort: true,
    });
    this.props.history.replace(queryStringResult);
  };

  handleSearchInput = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ): Promise<void> => {
    this.setState({ q: event.target.value, page: 1 });
    await this.performDelayedSearch();
  };

  handleStateFilter = (values: string[]): void => {
    this.setState({ selectedStates: values, page: 1 }, () => {
      this.performSearch();
    });
  };

  handleAssignedToFilter = (values: string[]): void => {
    this.setState({ selectedAssignedTo: values, page: 1 }, () => {
      this.performSearch();
    });
  };

  handleSortChange = (field: string, direction: string): void => {
    this.setState(
      { sortField: field, sortDirection: direction, page: 1 },
      () => {
        this.performSearch();
      },
    );
  };



  nextPage = async (page: number, pageSize?: number): Promise<void> => {
    if (!this._isMounted) return;

    const ps = typeof pageSize === "number" ? pageSize : this.state.pageSize;

    this.setState({ isLoading: true, page: page, pageSize: ps }, () => {
      if (this.state.selectedFieldsToDisplay.length < 1) {
        this.performSearch();
      } else {
        this.setState({ isLoading: false });
      }
    });
  };

  onSelectChange = (newSelectedIdsToExport: React.Key[]) => {
    this.setState({ selectedIdsToExport: newSelectedIdsToExport });
  };

  render(): JSX.Element | null {
    const {
      paymentInstructions,
      isLoading,
      paymentInstructionRequestsCount,
      page,
      selectedIdsToExport,
      selectedStates,
      stateOptions,
      selectedAssignedTo,
      assignedToOptions,
      sortField,
      sortDirection,
      sortFieldOptions,
      pageSize,
      q,
      showAdvancedSearchModal,
      selectedFieldsToDisplay,
      removedField,
    } = this.state;

    const rowSelection: RowSelection = {
      selectedIdsToExport,
      type: "checkbox",
      onChange: this.onSelectChange,
    };

    const tableData = paymentInstructions.map((instruction) => ({
      ...instruction,
      key: instruction.id,
    }));

    return (
      <React.Fragment>
        <AdvancedSearchModal
          toggleModal={this.toggleAdvancedSearchModel}
          showModal={showAdvancedSearchModal}
          model="fixedIncomePaymentInstruction"
          onError={this.handleOnAdvancedSearchError}
          onData={this.handleOnAdvancedSearchData}
          triggerField={removedField?.name}
          pageSize={pageSize}
          page={page}
          activeFields={selectedFieldsToDisplay}
        />
        <Card>
            <div className="departments-table-container">
              <PageToolbar
                advancedSearch={{
                  onOpen: this.toggleAdvancedSearchModel,
                  activeFields: selectedFieldsToDisplay,
                  onFieldRemove: this.handleOnFieldRemove,
                }}
                searchValue={q}
                onSearchChange={this.handleSearchInput}
                searchPlaceholder="Search payment instructions..."
                actions={
                  <ExportData
                  model={"fixedIncomePaymentInstruction"}
                  dataArray={paymentInstructions}
                  size={"large"}
                  customIds={selectedIdsToExport}
                />
              }
              totalCount={paymentInstructionRequestsCount}
              currentDataCount={paymentInstructions.length}
              page={page}
              pageSize={pageSize}
              selectedCount={selectedIdsToExport.length}
              filters={
                <PageToolbar.Filters
                  isFiltering={hasActiveFilters(this.state, { includeSort: true, includeAssignedTo: true })}
                  onClear={() => clearAllFilters(this, "fixedIncomePaymentInstruction")}
                  configs={[
                    {
                      label: "Status",
                      options: stateOptions,
                      selectedValues: selectedStates,
                      onChange: this.handleStateFilter,
                    },
                    {
                      label: "Assigned To",
                      options: assignedToOptions,
                      selectedValues: selectedAssignedTo,
                      onChange: this.handleAssignedToFilter,
                      onSearch: this.handleAssignedToSearch,
                      loading: this.state.isSearchingUsers,
                    },
                  ]}
                  sort={{
                    field: sortField,
                    direction: sortDirection,
                    options: sortFieldOptions,
                    onChange: this.handleSortChange,
                  }}
                />
              }
            modelName="fixed income payment instructions"
          />

          <Table
              loading={isLoading}
              scroll={{ x: 500 }}
              columns={paymentInstructionColumns}
              dataSource={tableData}
              pagination={{
                current: page,
                pageSize,
                total: paymentInstructionRequestsCount,
                onChange: this.nextPage,
                showSizeChanger: true,
                showTotal: showTotalPagination,
              }}
              rowSelection={rowSelection}
            />
          </div>
        </Card>
      </React.Fragment>
    );
  }
}
